<template>
  <div class="p-6 dark:text-white" v-if="profile">
    <!-- Profile Generation Notice -->
    <div
      v-if="showProfileNotice"
      class="bg-amber-100 border border-amber-400 text-amber-800 px-4 py-3 rounded relative mb-4 flex items-center justify-between"
    >
      <div>
        <strong class="font-bold">Reminder:</strong>
        <span class="ml-1">
          Profile changes require clicking "Generate Profiles" on the profiles list to take effect.
        </span>
      </div>
      <button @click="showProfileNotice = false" class="text-amber-800 hover:text-amber-900">
        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </button>
    </div>

    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <h1 class="text-2xl font-semibold flex items-center dark:text-white">
        <button
          @click="goBackToList"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back
        </button>
        Fortigate Profile: {{ profile.name }}
        <span v-if="!profile.org" class="ml-3 px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
          Global
        </span>
      </h1>

      <!-- Edit Profile Button -->
      <div class="flex space-x-2">
        <button
          v-if="canEdit || user?.is_staff"
          @click="$router.push({ name: 'EditFortigateProfile', params: { id: profile.id }, query: tenant.addSchemaParam() })"
          class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 transition duration-200"
        >
          Edit Profile
        </button>
        <button
          v-if="canDelete || user?.is_staff"
          @click="confirmDeleteProfile"
          class="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 transition duration-200"
        >
          Delete Profile
        </button>
      </div>
    </div>

    <!-- Profile Info Card -->
    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
      <h2 class="text-lg font-semibold mb-4">Profile Details</h2>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Name</label>
          <p class="font-medium font-mono">{{ profile.name }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Organisation</label>
          <p class="font-medium">{{ profile.org_name || 'Global (all orgs)' }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Created</label>
          <p class="font-medium">{{ formatDateTime(profile.created_date) }}</p>
        </div>
        <div>
          <label class="text-sm text-gray-500 dark:text-gray-400">Last Modified</label>
          <p class="font-medium">{{ formatDateTime(profile.last_modified) }}</p>
        </div>
      </div>
      <div v-if="profile.description" class="mt-4">
        <label class="text-sm text-gray-500 dark:text-gray-400">Description</label>
        <p class="font-medium">{{ profile.description }}</p>
      </div>
    </div>

    <!-- Tabs (mirrors ProbeDetails tab styling) -->
    <div class="glass-card mb-2">
      <nav class="flex px-2 py-2">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          @click="activeTab = tab.key"
          :class="[
            'ml-2 group inline-flex items-center px-4 py-2 border-t border-l border-r rounded-t-md text-sm font-medium transition-colors duration-150 ease-in-out',
            activeTab === tab.key
              ? 'bg-white text-blue-600 border-gray-300 -mb-px dark:bg-gray-800 dark:text-blue-400'
              : 'bg-gray-100 text-gray-500 border-transparent hover:bg-gray-50 hover:text-blue-600 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600 dark:hover:text-blue-400',
          ]"
        >
          {{ tab.label }}
          <span class="ml-2 px-2 py-0.5 text-xs rounded-full border border-gray-300/60 dark:border-gray-600/60">
            {{ getCount(tab.key) }}
          </span>
        </button>
      </nav>
    </div>

    <!-- Tab Content -->
    <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">

      <!-- Tab Content -->
      <div class="p-6">
        <!-- URL Paths Tab -->
        <div v-if="activeTab === 'probes'">
          <!-- Add URL Path Section -->
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">URL Paths</h3>
            <div class="flex space-x-2" v-if="canEdit || user?.is_staff">
              <button
                @click="showAddProbeModal('include')"
                class="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
              >
                Add Include Path
              </button>
              <button
                @click="showAddProbeModal('exclude')"
                class="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 text-sm"
              >
                Add Exclude Path
              </button>
            </div>
          </div>

          <!-- Include URL Paths Section -->
          <div class="mb-6">
            <h4 class="text-md font-semibold mb-2 text-green-700 dark:text-green-400 flex items-center">
              <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              Include URL Paths ({{ includeProbes.length }})
            </h4>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-3 italic">
              Note: If no include paths are defined, all paths will be included by default.
            </p>
            <div v-if="includeProbes.length > 0" class="space-y-2">
              <div
                v-for="probe in includeProbes"
                :key="probe.id"
                class="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg"
              >
                <span class="font-mono text-sm">{{ probe.probe_path }}</span>
                <button
                  v-if="canEdit || user?.is_staff"
                  @click="deleteProbe(probe)"
                  class="text-red-600 hover:text-red-800 p-1"
                  title="Delete"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fill-rule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <p v-else class="text-gray-500 text-sm py-4">
              No include URL paths defined. All paths will run by default unless excluded.
            </p>
          </div>

          <!-- Exclude URL Paths Section -->
          <div>
            <h4 class="text-md font-semibold mb-3 text-red-700 dark:text-red-400 flex items-center">
              <svg class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              Exclude URL Paths ({{ excludeProbes.length }})
            </h4>
            <div v-if="excludeProbes.length > 0" class="space-y-2">
              <div
                v-for="probe in excludeProbes"
                :key="probe.id"
                class="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
              >
                <span class="font-mono text-sm">{{ probe.probe_path }}</span>
                <button
                  v-if="canEdit || user?.is_staff"
                  @click="deleteProbe(probe)"
                  class="text-red-600 hover:text-red-800 p-1"
                  title="Delete"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fill-rule="evenodd"
                      d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
            <p v-else class="text-gray-500 text-sm py-4">No exclude URL paths defined.</p>
          </div>
        </div>

        <!-- YAML Preview Tab -->
        <div v-if="activeTab === 'yaml'">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-semibold">YAML Preview</h3>
            <button @click="copyYaml" class="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 text-sm">
              Copy YAML
            </button>
          </div>
          <pre class="border border-gray-300/40 dark:border-gray-600/40 p-4 rounded-lg overflow-x-auto text-sm font-mono">{{ yamlPreview }}</pre>
        </div>
      </div>
    </div>

    <!-- Add URL Path Modal -->
    <div v-if="addingProbe" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">
          Add {{ addingProbe.type === 'include' ? 'Include' : 'Exclude' }} URL Path
        </h3>

        <!-- Common URL Paths Dropdown -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-2">Select from common URL paths:</label>
          <GlassSelect
            v-model="selectedCommonProbes"
            :options="availableCommonProbes"
            multiple
            searchable
            searchPlaceholder="Search URL paths..."
            placeholder="-- Select common URL paths --"
          />
        </div>

        <div class="text-center text-gray-500 text-sm mb-4">- AND / OR -</div>

        <!-- Custom URL Path Input -->
        <div class="mb-4">
          <label class="block text-sm font-medium mb-2">Enter custom URL path:</label>
          <input
            v-model="newProbePath"
            type="text"
            placeholder="e.g., System/Status, VirtualWAN/HealthCheck"
            class="w-full border border-gray-300 dark:bg-gray-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p class="text-xs text-gray-500 mt-1">URL paths are case-sensitive and use prefix matching.</p>
        </div>

        <div class="flex justify-end space-x-2">
          <button @click="addingProbe = null" class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
            Cancel
          </button>
          <button
            @click="confirmAddProbe"
            :disabled="!pathsToAdd.length || isAddingProbes"
            :class="[
              'px-4 py-2 rounded',
              pathsToAdd.length && !isAddingProbes
                ? (addingProbe.type === 'include' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700') + ' text-white'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            ]"
          >
            {{ isAddingProbes ? 'Adding...' : pathsToAdd.length > 1 ? `Add ${pathsToAdd.length} Paths` : 'Add Path' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="deletingItem" class="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white/80 dark:bg-gray-800/70 backdrop-blur-md p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
        <h3 class="text-lg font-semibold mb-4">Confirm Delete</h3>
        <p class="mb-4">Are you sure you want to delete this URL path?</p>
        <p class="font-medium text-gray-600 dark:text-gray-300 mb-6 font-mono">
          {{ deletingItem.probe_path }}
        </p>
        <div class="flex justify-end space-x-2">
          <button @click="deletingItem = null" class="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400">
            Cancel
          </button>
          <button @click="confirmDelete" class="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <div
      v-if="toastMessage"
      :class="[
        'fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg transition-opacity duration-300 z-50',
        toastType === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
      ]"
    >
      {{ toastMessage }}
    </div>
  </div>

  <!-- Loading State -->
  <div v-else-if="!error" class="p-6">
    <p>Loading...</p>
  </div>

  <!-- Error State -->
  <PageError v-if="error" :error="error" :errorCode="errorCode" />
</template>

<script>
import {
  getFortigateProfileById,
  deleteFortigateProfile,
  deleteFortigateProbe,
  createFortigateProbe,
  getCommonProbes,
} from "@/services/fortigateProfileService";
import PageError from "@/components/PageError.vue";
import GlassSelect from "@/components/GlassSelect.vue";
import { useStore } from "@/store";
import { formatError } from "@/utils/errorMessages";
import { useMultiTenant } from "@/composables/useMultiTenant";

export default {
  name: "FortigateProfileDetails",
  components: {
    PageError,
    GlassSelect,
  },
  setup() {
    const tenant = useMultiTenant();
    return { tenant };
  },
  data() {
    return {
      profile: null,
      error: "",
      errorCode: "",
      activeTab: "probes",
      deletingItem: null,
      addingProbe: null,
      newProbePath: "",
      selectedCommonProbes: [],
      isAddingProbes: false,
      commonProbes: [],
      canEdit: false,
      canDelete: false,
      showProfileNotice: false,
      toastMessage: "",
      toastType: "success",
      tabs: [
        { key: "probes", label: "URL Paths" },
        { key: "yaml", label: "YAML Preview" },
      ],
    };
  },
  computed: {
    user() {
      return useStore.getUser();
    },
    currentSchema() {
      return this.$route.query.schemaName || this.tenant.currentSchemaName?.value || null;
    },
    includeProbes() {
      return (this.profile?.probes || []).filter(p => p.probe_type === 'include');
    },
    excludeProbes() {
      return (this.profile?.probes || []).filter(p => p.probe_type === 'exclude');
    },
    availableCommonProbes() {
      const existingPaths = (this.profile?.probes || []).map(p => p.probe_path);
      return this.commonProbes.filter(p => !existingPaths.includes(p));
    },
    pathsToAdd() {
      const custom = this.newProbePath.trim();
      return [...new Set(custom ? [...this.selectedCommonProbes, custom] : this.selectedCommonProbes)];
    },
    yamlPreview() {
      if (!this.profile) return "";

      const profileData = {
        [this.profile.name]: {
          probes: {},
        },
      };

      if (this.includeProbes.length > 0) {
        profileData[this.profile.name].probes.include = this.includeProbes.map(p => p.probe_path);
      }
      if (this.excludeProbes.length > 0) {
        profileData[this.profile.name].probes.exclude = this.excludeProbes.map(p => p.probe_path);
      }

      return this.toYaml(profileData);
    },
  },
  async mounted() {
    await this.tenant.fetchOrganizations();
    await this.tenant.loadInitialData(() => Promise.resolve());
    await this.fetchProfile();
    await this.fetchCommonProbes();
    await this.checkPermissions();
    // Check for profile notice flag from AddFortigateProfile redirect
    if (this.$route.query.showProfileNotice) {
      this.showProfileNotice = true;
    }
  },
  methods: {
    async checkPermissions() {
      if (this.user) {
        this.canEdit = await useStore.hasPermission("Can change Fortigate Exporter Profile");
        this.canDelete = await useStore.hasPermission("Can delete Fortigate Exporter Profile");
      }
    },
    goBackToList() {
      // Pass profile notice flag if it was shown on this page
      const query = { ...this.tenant.addSchemaParam(), ...(this.showProfileNotice ? { showProfileNotice: true } : {}) };
      this.$router.push({ name: "FortigateProfiles", query });
    },
    async fetchProfile() {
      try {
        const profileId = this.$route.params.id;
        this.profile = await getFortigateProfileById(profileId, this.currentSchema);
      } catch (error) {
        console.error("Error fetching Fortigate profile:", error);
        const { code, message } = formatError(error);
        this.errorCode = code;
        this.error = message;
      }
    },
    async fetchCommonProbes() {
      try {
        const result = await getCommonProbes(this.currentSchema);
        this.commonProbes = Array.isArray(result) ? result.map(p => p.path) : (result.probes || []);
      } catch (error) {
        console.error("Error fetching common probes:", error);
      }
    },
    getCount(tabKey) {
      if (!this.profile) return 0;
      const countMap = {
        probes: (this.profile.probes?.length || 0),
        yaml: "-",
      };
      return countMap[tabKey];
    },
    formatDateTime(dateString) {
      if (!dateString) return "N/A";
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleString(undefined, options);
    },
    showAddProbeModal(type) {
      this.addingProbe = { type };
      this.newProbePath = "";
      this.selectedCommonProbes = [];
    },
    async confirmAddProbe() {
      const paths = this.pathsToAdd;
      if (!paths.length || !this.addingProbe) return;

      this.isAddingProbes = true;
      try {
        // No bulk endpoint: one request per path. Failed paths stay selected
        // so they can be retried or removed.
        const results = await Promise.allSettled(paths.map((probe_path) =>
          createFortigateProbe({
            profile: this.profile.id,
            probe_path,
            probe_type: this.addingProbe.type,
          }, this.currentSchema)
        ));
        const failed = [];
        results.forEach((result, i) => {
          if (result.status === "rejected") {
            const data = result.reason?.response?.data;
            failed.push({
              path: paths[i],
              reason: data?.probe_path?.[0] || data?.non_field_errors?.[0] || data?.detail || "Failed to add URL path",
            });
          }
        });
        const added = paths.length - failed.length;
        if (added) {
          await this.fetchProfile();
          this.showProfileNotice = true;
        }

        if (!failed.length) {
          this.addingProbe = null;
          this.newProbePath = "";
          this.selectedCommonProbes = [];
          this.showToast(added === 1 ? "URL path added successfully" : `${added} URL paths added successfully`, "success");
          return;
        }
        console.error("Error adding probes:", failed);
        const failedPaths = failed.map((f) => f.path);
        this.selectedCommonProbes = this.selectedCommonProbes.filter((p) => failedPaths.includes(p));
        if (!failedPaths.includes(this.newProbePath.trim())) this.newProbePath = "";
        const detail = failed.map((f) => `${f.path}: ${f.reason}`).join("; ");
        this.showToast(added ? `Added ${added} of ${paths.length} URL paths. Failed: ${detail}` : detail, "error");
      } finally {
        this.isAddingProbes = false;
      }
    },
    deleteProbe(probe) {
      this.deletingItem = probe;
    },
    async confirmDelete() {
      if (!this.deletingItem) return;

      try {
        await deleteFortigateProbe(this.deletingItem.id, this.currentSchema);
        await this.fetchProfile();
        this.deletingItem = null;
        this.showProfileNotice = true;
        this.showToast("URL path deleted successfully", "success");
      } catch (error) {
        console.error("Error deleting probe:", error);
        this.showToast("Failed to delete URL path", "error");
      }
    },
    async confirmDeleteProfile() {
      if (confirm(`Are you sure you want to delete profile "${this.profile.name}"? This will also delete all URL paths.`)) {
        try {
          await deleteFortigateProfile(this.profile.id, this.currentSchema);
          this.$router.push({ name: "FortigateProfiles", query: this.tenant.addSchemaParam() });
        } catch (error) {
          console.error("Error deleting profile:", error);
          this.showToast("Failed to delete profile", "error");
        }
      }
    },
    showToast(message, type = "success") {
      this.toastMessage = message;
      this.toastType = type;
      setTimeout(() => {
        this.toastMessage = "";
      }, 3000);
    },
    copyYaml() {
      navigator.clipboard.writeText(this.yamlPreview).then(() => {
        this.showToast("YAML copied to clipboard", "success");
      });
    },
    toYaml(obj, indent = 0) {
      const spaces = "  ".repeat(indent);
      let yaml = "";

      for (const [key, value] of Object.entries(obj)) {
        if (Array.isArray(value)) {
          yaml += `${spaces}${key}:\n`;
          for (const item of value) {
            if (typeof item === "object") {
              yaml += `${spaces}  - `;
              const itemYaml = this.toYaml(item, indent + 2).trim();
              const lines = itemYaml.split("\n");
              yaml += lines[0] + "\n";
              for (let i = 1; i < lines.length; i++) {
                yaml += `${spaces}    ${lines[i]}\n`;
              }
            } else {
              yaml += `${spaces}  - ${item}\n`;
            }
          }
        } else if (typeof value === "object" && value !== null) {
          yaml += `${spaces}${key}:\n`;
          yaml += this.toYaml(value, indent + 1);
        } else {
          yaml += `${spaces}${key}: ${value}\n`;
        }
      }

      return yaml;
    },
  },
};
</script>
